import React, { useCallback, useEffect, useMemo, useState } from "react";
import Maximize from "../../Maximize";
import LineChart, { LineChartProps } from "../../LineChart";
import { InfoCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { message, Select, Typography } from "antd";
import { TableRecord } from "../../Table";
import axiosInstance from "../../../utils/axiosInstance";
type SaturationCurvesProps = {
  jobId: string;
};

const SaturationCurves = ({ jobId }: SaturationCurvesProps) => {
  const [mmmOutputJson, setMmmOutputJson] = useState<any>(null);
  const [mediaVariables, setMediaVariables] = useState<string[]>([]);
  const [selectedSaturation, setSelectedSaturation] = useState<
    string | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axiosInstance
      .get(`/api/jobConfig/${jobId}`)
      .then((response) => {
        const mediaVariables =
          response.data.dataMappingConfig.mappings.mmm.columnMapping
            .media_variables;
        setMediaVariables(mediaVariables);
        axiosInstance
          .get(`/api/mmm/output/${jobId}`)
          .then((outputResponse) => {
            let outputJson = outputResponse.data.result;
            if (typeof outputJson === "string") {
              try {
                outputJson = JSON.parse(outputJson);
              } catch (error) {
                console.error("Failed to parse JSON string:", error);
              }
            }
            setMmmOutputJson(outputJson);
            try {
              const adstock = outputJson["adstock"];
              if (adstock && Array.isArray(adstock) && adstock.length > 0) {
                console.log("Found adstock data:", adstock.slice(0, 2));

                // Check the structure of the adstock data
                const firstItem = adstock[0];

                // Determine the format of the adstock data
                let groupedData: Record<string, any> = {};

                // Format 1: {Var_Name: string, Adstock_Value: number}
                if (
                  firstItem.Var_Name &&
                  firstItem.Adstock_Value !== undefined
                ) {
                  groupedData = adstock.reduce((acc: any, item: any) => {
                    // Split the variable name into components
                    const prefix = item.Var_Name;
                    // Extract prefix (everything before the last component)
                    const metric = item.Type;

                    // Add the Adstock_Value to the correct metric
                    acc[prefix][metric] = item.Adstock_Value;

                    return acc;
                  }, {});
                }
                // If we have grouped data, create the chart
                if (Object.keys(groupedData).length > 0) {
                  const labels = Object.keys(groupedData);

                  // Determine which metrics are available
                  const availableMetrics = new Set<string>();
                  for (const label of labels) {
                    for (const metric of Object.keys(groupedData[label])) {
                      //if (groupedData[label][metric] !== 0)
                      {
                        availableMetrics.add(metric);
                      }
                    }
                  }

                  // Create datasets based on available metrics
                  const barDatasets: any[] = [];

                  // Add theta/adstock if available
                  if (availableMetrics.has("theta")) {
                    barDatasets.push({
                      name: "Adstock",
                      values: labels.map((label) => groupedData[label]?.theta),
                      color: "#73C2FB", // Blue
                    });
                  }

                  // Add alpha/strength if available
                  if (availableMetrics.has("alpha")) {
                    barDatasets.push({
                      name: "alpha",
                      values: labels.map((label) => groupedData[label]?.alpha),
                      color: "#FF6F61", // Red
                    });
                  }

                  // Add gamma/length if available
                  if (availableMetrics.has("gamma")) {
                    barDatasets.push({
                      name: "gamma",
                      values: labels.map((label) => groupedData[label]?.gamma),
                      color: "#32CD32", // Green
                    });
                  }

                  // If no specific metrics were found, use a generic approach
                  if (barDatasets.length === 0) {
                    // Just use the first numeric property we find
                    const firstLabel = labels[0];
                    const firstMetric = Object.keys(
                      groupedData[firstLabel]
                    ).find(
                      (key) => typeof groupedData[firstLabel][key] === "number"
                    );

                    if (firstMetric) {
                      barDatasets.push({
                        name:
                          firstMetric.charAt(0).toUpperCase() +
                          firstMetric.slice(1),
                        values: labels.map(
                          (label) => groupedData[label]?.[firstMetric]
                        ),
                        color: "#73C2FB", // Blue
                      });
                    }
                  }
                }
              }
            } catch (error) {
              console.error("Error processing adstock data:", error);
            }

            const sortedContributionsChart =
              outputJson.Contributions_chart?.sort((a: any, b: any) => {
                const isAImportant =
                  a.Variables === "Seasonality" || a.Variables === "Base";
                const isBImportant =
                  b.Variables === "Seasonality" || b.Variables === "Base";

                if (isAImportant && isBImportant) {
                  return b.Contributions - a.Contributions;
                }

                if (isAImportant) return -1;
                if (isBImportant) return 1;

                return 0;
              });

            const formattedLabels: string[] = [];
            const formattedData: number[] = [];

            sortedContributionsChart?.forEach((item: any) => {
              const value = Number.parseInt(item.Contributions);
              const formattedValue =
                value >= 100000
                  ? `$${(value / 1000).toFixed(0)}K`
                  : `$${value}`;

              formattedLabels.push(`${item.Variables} (${formattedValue})`);
              formattedData.push(value); // keep numeric if your chart expects numbers
            });

            if (outputJson) {
              const colors = [
                "rgba(75, 192, 192, 1)",
                "rgba(255, 99, 132, 1)",
                "rgba(54, 162, 235, 1)",
                "rgba(255, 206, 86, 1)",
                "rgba(153, 102, 255, 1)",
                "rgba(255, 159, 64, 1)",
              ];
              const options = Object.keys(outputJson)
                .filter((key) => key.startsWith("Saturation_"))
                .map((key, idx) => {
                  const data = outputJson[key];
                  if (!data || !Array.isArray(data) || data.length === 0)
                    return null;
                  const datasetName = key
                    .replace("Saturation_", "")
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                    .replace(/_/g, " ");
                  const color = colors[idx % colors.length];
                  const labels = data.map((item: any) => item?.Spends ?? 0);
                  const lineData = data.map((item: any) => {
                    const fieldName = key.replace("Saturation_", "");
                    return item?.[fieldName] ?? 0;
                  });
                  const point = {
                    x: labels[labels.length - 1],
                    y: lineData[lineData.length - 1],
                  };
                  return {
                    key,
                    label: datasetName,
                    chartProps: {
                      labels,
                      dataSets: [
                        {
                          name: datasetName,
                          data: lineData,
                          borderColor: color,
                          backgroundColor: color.replace("1)", "0.3)"),
                        },
                      ],
                      xAxisTitle: "Spends",
                      yAxisTitle: datasetName,
                      point,
                    },
                  };
                })
                .filter(Boolean);
            }
            setLoading(false);
          })
          .catch((error: any) => {
            console.error("Error fetching MMM output data:", error);
            message.error("Failed to fetch analysis data");
            setLoading(false);
          });
      })
      .catch((error: any) => {
        console.error("Error fetching job config:", error);
        message.error("Failed to fetch job configuration");
        setLoading(false);
      });
  }, []);
  const saturationChartOptions = useMemo(() => {
    if (!mmmOutputJson) return [];

    const colors = [
      "rgba(75, 192, 192, 1)",
      "rgba(255, 99, 132, 1)",
      "rgba(54, 162, 235, 1)",
      "rgba(255, 206, 86, 1)",
      "rgba(153, 102, 255, 1)",
      "rgba(255, 159, 64, 1)",
    ];

    const extractSaturationData = (key: string, data: any[]) => {
      const variableName = key.replace("Saturation_", "");
      if (
        data[0]?.Spends !== undefined &&
        data[0]?.[variableName] !== undefined
      ) {
        return {
          labels: data.map((item) =>
            item.Spends.startsWith("Line")
              ? item.Spends.slice("Line".length)
              : item.Spends
          ),
          values: data.map((item) => item[variableName]),
        };
      }
      if (
        data[0]?.mean_input_total !== undefined &&
        data[0]?.[key] !== undefined
      ) {
        return {
          labels: data.map((item) => item.mean_input_total),
          values: data.map((item) => item[key]),
        };
      }
      if (data[0]?.x !== undefined && data[0]?.y !== undefined) {
        return {
          labels: data.map((item) => item.x),
          values: data.map((item) => item.y),
        };
      }

      // Approach 4: Check if data has input and output properties
      if (data[0]?.input !== undefined && data[0]?.output !== undefined) {
        return {
          labels: data.map((item) => item.input),
          values: data.map((item) => item.output),
        };
      }

      // Approach 5: Check if data is an array of arrays [x, y]
      if (Array.isArray(data[0]) && data[0].length === 2) {
        return {
          labels: data.map((item) => item[0]),
          values: data.map((item) => item[1]),
        };
      }

      // Fallback: Try to find any numeric properties
      const firstItem = data[0];
      const numericKeys = Object.keys(firstItem).filter(
        (k) => typeof firstItem[k] === "number" && k !== "id" && k !== "index"
      );

      if (numericKeys.length >= 2) {
        // Use the first two numeric properties as x and y
        return {
          labels: data.map((item) => item[numericKeys[0]]),
          values: data.map((item) => item[numericKeys[1]]),
        };
      }

      // Last resort: Generate sequential x values and use any available numeric value for y
      if (numericKeys.length === 1) {
        return {
          labels: Array.from({ length: data.length }, (_, i) => i + 1),
          values: data.map((item) => item[numericKeys[0]]),
        };
      }

      // If all else fails, return empty arrays
      return { labels: [], values: [] };
    };

    return Object.keys(mmmOutputJson)
      .filter((key) => key.startsWith("Saturation_"))
      .map((key, idx) => {
        const data = mmmOutputJson[key];
        if (!data || !Array.isArray(data) || data.length === 0) return null;

        // Format the dataset name properly
        const datasetName = key
          .replace("Saturation_", "")
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")
          .replace(/_/g, " ");

        const color = colors[idx % colors.length];

        // Extract data using our helper function
        const { labels, values } = extractSaturationData(key, data);

        // Skip if we couldn't extract any meaningful data
        if (labels.length === 0 || values.length === 0) return null;

        // Create a point for the last data point (used for annotations)
        const point = {
          x: labels[labels.length - 1],
          y: values[values.length - 1],
        };

        return {
          key,
          label: datasetName.startsWith("Line")
            ? datasetName.slice("Line".length)
            : datasetName,
          chartProps: {
            labels,
            dataSets: [
              {
                name: datasetName,
                data: values,
                borderColor: color,
                backgroundColor: color.replace("1)", "0.3)"),
              },
            ],
            xAxisTitle: "Spends",
            yAxisTitle: "Response",
            point,
          },
        };
      })
      .filter(
        (v): v is { key: string; label: string; chartProps: any } => v !== null
      );
  }, [mmmOutputJson]);
  const generateFallbackSaturationData = useCallback(() => {
    if (saturationChartOptions.length === 0 && mediaVariables.length > 0) {
      // Create synthetic data for demonstration
      const fallbackData = mediaVariables.map((variable, idx) => {
        const color = [
          "rgba(75, 192, 192, 1)",
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ][idx % 6];
        const points = 10;
        const labels = Array.from({ length: points }, (_, i) =>
          Math.round((i + 1) * 10000)
        );

        // Create a curve that shows diminishing returns
        const lineData = labels.map((_, i) => {
          const x = i / (points - 1);
          // Formula for diminishing returns curve: y = 1 - e^(-3x)
          return (1 - Math.exp(-3 * x)) * 100;
        });

        return {
          key: `Saturation_${variable}`,
          label: variable
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          chartProps: {
            labels,
            dataSets: [
              {
                name: variable,
                data: lineData,
                borderColor: color,
                backgroundColor: color.replace("1)", "0.3)"),
              },
            ],
            xAxisTitle: "Spends",
            yAxisTitle: "Response",
            point: {
              x: labels[labels.length - 1],
              y: lineData[lineData.length - 1],
            },
          },
        };
      });

      return fallbackData;
    }
    return [];
  }, [saturationChartOptions.length, mediaVariables]);
  useEffect(() => {
    if (saturationChartOptions.length > 0 && !selectedSaturation) {
      setSelectedSaturation(saturationChartOptions[0].key);
    }
  }, [saturationChartOptions, selectedSaturation]);

  return (
    <div>
      <div>
        <h1 className="text-2xl font-bold text-center my-8">
          Saturation Curves
        </h1>
        {loading ? (
          <div className="text-center p-8 bg-gray-50 rounded-lg">
            <div className="flex flex-col items-center justify-center">
              <ReloadOutlined className="text-4xl text-blue-500 mb-4 animate-spin" />
              <Typography.Text type="secondary">
                Loading saturation curve data...
              </Typography.Text>
            </div>
          </div>
        ) : (() => {
          const displayOptions =
            saturationChartOptions.length > 0
              ? saturationChartOptions.filter(
                  (ele) => !ele.label.toLocaleLowerCase().includes("point")
                )
              : generateFallbackSaturationData();

          // If we have options to display (either real or fallback)
          if (displayOptions.length > 0) {
            // Make sure we have a selected option
            const currentSelection =
              selectedSaturation || displayOptions[0].key;
            if (selectedSaturation !== currentSelection) {
              setSelectedSaturation(currentSelection);
            }

            // Find the currently selected option
            const displayOption =
              displayOptions.find((opt) => opt.key === currentSelection) ||
              displayOptions[0];

            return (
              <>
                <div className="mb-4 flex justify-center">
                  <Select
                    style={{ minWidth: 240 }}
                    value={currentSelection}
                    onChange={setSelectedSaturation}
                    options={displayOptions.map((opt) => ({
                      value: opt.key,
                      label: opt.label,
                    }))}
                  />
                </div>
                <div>
                  {displayOption && displayOption.chartProps ? (
                    <div>
                      <Maximize title={displayOption.label}>
                        <LineChart {...displayOption.chartProps} />
                      </Maximize>
                      <div className="mt-4 text-center text-sm text-gray-500">
                        <InfoCircleOutlined className="mr-1" />
                        This chart shows the relationship between spending and
                        response for {displayOption.label}. The curve
                        demonstrates diminishing returns as spending increases.
                        {saturationChartOptions.length === 0 && (
                          <div className="mt-2 text-amber-500">
                            <ReloadOutlined className="mr-1" />
                            Note: Using sample data for visualization. Actual
                            data will be shown when available.
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-8 bg-gray-50 rounded-lg">
                      <Typography.Text type="secondary">
                        <InfoCircleOutlined className="mr-2" />
                        No saturation curve data available for the selected
                        option.
                      </Typography.Text>
                    </div>
                  )}
                </div>
              </>
            );
          } else {
            // No data available at all
            return (
              <div className="text-center p-8 bg-gray-50 rounded-lg">
                <Typography.Text type="secondary">
                  <InfoCircleOutlined className="mr-2" />
                  No saturation curve data available. Please check your data
                  source.
                </Typography.Text>
              </div>
            );
          }
        })()}
      </div>
    </div>
  );
};

export default SaturationCurves;
